import { isNativeApp, isIOS, webOrigin } from "@/lib/nativeBridge";
import IosPurchaseNotice from "@/components/IosPurchaseNotice";
import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { StorefrontIcon as Storefront, PlusIcon as Plus, TrashIcon as Trash, CameraIcon as Camera, ArrowRightIcon as ArrowRight, CheckCircleIcon as CheckCircle, CircleIcon as Circle, WarningIcon as Warning, PaintBrushIcon as PaintBrush, HourglassIcon as Hourglass, XCircleIcon as XCircle, ShareNetworkIcon as ShareNetwork, CopyIcon as Copy } from "@phosphor-icons/react";
import { toast } from "sonner";
import api from "@/lib/api";
import { usePublicPrices } from "@/lib/usePublicPrices";
import { thumbUrl } from "@/lib/utils-app";
import AppShell from "@/components/AppShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import PrintifyTab from "@/pages/store/PrintifyTab";
import CompBanner from "@/components/CompBanner";
import StripeTaxStatusBanner from "@/components/StripeTaxStatusBanner";

/**
 * Multi-tenant Kennel Storefront — owner-side management.
 *
 * Onboarding gate-cascade (in order):
 *   1. Active `storefront` subscription   → otherwise route to /billing
 *   2. Completed Stripe Connect payouts   → otherwise route to /payouts
 *   3. Store record created               → otherwise show the slug
 *                                            claim form
 * Once all three are satisfied we render the full product + orders dashboard.
 */
export default function MyStore() {
  const navigate = useNavigate();
  // `?tab=orders` (or any other tab key below) lets us deep-link a
  // seller straight into their orders dashboard from email/inbox/push
  // CTAs — fixes the "Lauren saw the inbox ping but didn't know how
  // to mark an order delivered" UX gap from 2026-02.
  const [searchParams, setSearchParams] = useSearchParams();
  const initialTab = searchParams.get("tab") || "products";
  const [tab, setTab] = useState(initialTab);
  useEffect(() => {
    const urlTab = searchParams.get("tab");
    if (urlTab && urlTab !== tab) setTab(urlTab);
  }, [searchParams, tab]);
  const onTabChange = (value) => {
    setTab(value);
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set("tab", value);
      return next;
    }, { replace: true });
  };
  const [state, setState] = useState(null);
  const [loading, setLoading] = useState(true);

  const reload = async () => {
    try {
      const { data } = await api.get("/me/store");
      // Guard: a 200 with null body would set state to null and crash
      // the gate cascade below (`state.subscription_active` on null).
      setState(data ?? { subscription_active: false, payouts_ready: false, store: null, ready_to_sell: false });
    } catch (err) {
      const code = err?.response?.data?.detail?.code;
      if (err?.response?.status === 402 || code === "storefront_subscription_required") {
        setState({ subscription_active: false, payouts_ready: false, store: null, ready_to_sell: false });
      } else {
        toast.error("Failed to load your store");
        // CRITICAL: leave `state` as null and the early-return at line ~58
        // dereferences `state.subscription_active` → crash. Set a safe
        // default so the gate cascade renders the upsell card instead.
        setState({ subscription_active: false, payouts_ready: false, store: null, ready_to_sell: false });
      }
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { reload(); }, []);

  if (loading) {
    return (
      <AppShell>
        <div className="max-w-5xl mx-auto p-6 text-[#6B6B6B]">Loading…</div>
      </AppShell>
    );
  }

  // Stage 1: no subscription → upsell card.
  if (!state.subscription_active) {
    return (
      <AppShell>
        <div className="max-w-3xl mx-auto p-6 space-y-5">
          <LaunchChecklist step={1} />
          <FeeMathBanner />
          <UpsellCard onSubscribe={() => navigate("/billing")} fee={state.platform_fee_pct} />
        </div>
      </AppShell>
    );
  }

  // Stage 2: subscribed but no payouts yet → Connect onboarding nudge.
  if (!state.payouts_ready) {
    return (
      <AppShell>
        <div className="max-w-3xl mx-auto p-6 space-y-5">
          <LaunchChecklist step={2} />
          <FeeMathBanner />
          <ConnectNudge onGo={() => navigate("/payouts")} />
        </div>
      </AppShell>
    );
  }

  // Stage 3: subscribed + payouts ready, but no store record → slug claim.
  if (!state.store) {
    return (
      <AppShell>
        <div className="max-w-2xl mx-auto p-6 space-y-5">
          <LaunchChecklist step={3} />
          <FeeMathBanner />
          <ClaimSlugForm onCreated={reload} />
        </div>
      </AppShell>
    );
  }

  return (
    <AppShell>
      <div className="max-w-6xl mx-auto p-6" data-testid="my-store-dashboard">
        <CompBanner className="mb-4" />
        <StripeTaxStatusBanner persona="storefront" />
        {state.is_suspended && (
          <div className="rounded-2xl border border-amber-300 bg-amber-50 p-5 mb-5" data-testid="suspended-banner">
            <p className="font-spectral text-xl text-amber-900 flex items-center gap-2">
              <Warning size={20} weight="duotone" /> Your storefront is suspended
            </p>
            <p className="text-[13px] text-amber-900 mt-2 leading-relaxed">
              <strong>Reason on file:</strong> {state.suspension_reason || "—"}
            </p>
            <p className="text-[12px] text-amber-800 mt-3">
              Your public page is hidden, you can&rsquo;t add or edit products, and pending orders were cancelled.
              You can appeal in writing within 14 days to{" "}
              <a href="mailto:hello@armstrongk9registry.com" className="underline font-semibold">hello@armstrongk9registry.com</a>.
              Review the <a href="/storefront-seller-agreement" className="underline">Seller Agreement</a> for the full policy.
            </p>
          </div>
        )}
        <StoreHeader store={state.store} platformFeePct={state.platform_fee_pct} isFeatured={state.is_featured} featuredUntil={state.is_featured_until} />
        <Tabs value={tab} onValueChange={onTabChange} className="mt-6">
          <TabsList>
            <TabsTrigger value="products" data-testid="tab-products">Products</TabsTrigger>
            <TabsTrigger value="orders" data-testid="tab-orders">Orders</TabsTrigger>
            <TabsTrigger value="merch" data-testid="tab-merch">Merch</TabsTrigger>
            <TabsTrigger value="custom" data-testid="tab-custom">Custom orders</TabsTrigger>
            <TabsTrigger value="branding" data-testid="tab-branding">Branding</TabsTrigger>
            <TabsTrigger value="featured" data-testid="tab-featured">Featured</TabsTrigger>
            <TabsTrigger value="settings" data-testid="tab-settings">Settings</TabsTrigger>
            <TabsTrigger value="help" data-testid="tab-help">Help</TabsTrigger>
          </TabsList>
          <TabsContent value="products"><ProductsTab /></TabsContent>
          <TabsContent value="orders"><OrdersTab /></TabsContent>
          <TabsContent value="merch"><PrintifyTab /></TabsContent>
          <TabsContent value="custom"><MyStoreCustomOrdersTab slug={state.store?.slug} /></TabsContent>
          <TabsContent value="branding"><BrandingTab store={state.store} onSaved={reload} /></TabsContent>
          <TabsContent value="featured"><FeaturedTab isFeatured={state.is_featured} featuredUntil={state.is_featured_until} /></TabsContent>
          <TabsContent value="settings"><SettingsTab store={state.store} onSaved={reload} /></TabsContent>
          <TabsContent value="help"><div className="py-6"><StripeHowItWorks /></div></TabsContent>
        </Tabs>
      </div>
    </AppShell>
  );
}

// ---------------------------------------------------- Launch checklist
/**
 * Four-step progress bar shown at the top of every pre-active stage.
 * `step` (1-indexed) marks the *current* step — earlier ones render as
 * green checks, later ones as gray pending circles. Each step also
 * carries a one-line tip so the seller always knows what to do next.
 */
const LAUNCH_STEPS_DEFAULTS = [
  { label: "Subscribe", tip: "Unlock the Storefront feature with an active Dog Shop plan." },
  { label: "Set up Stripe payouts", tip: "Connect your bank so customer payments reach you. ~3–5 min via Stripe Express." },
  { label: "Claim your URL", tip: "Pick the slug your customers will share (e.g. /store/your-kennel)." },
  { label: "Add your first product", tip: "Upload a photo, set a price, and you're live in the directory." },
];

function useLaunchSteps() {
  const { prices } = usePublicPrices();
  const mo = prices("storefront_monthly", "$19");
  return [
    { label: "Subscribe", tip: `Unlock the Storefront feature for ${mo}/mo on /billing.` },
    ...LAUNCH_STEPS_DEFAULTS.slice(1),
  ];
}

function LaunchChecklist({ step }) {
  const LAUNCH_STEPS = useLaunchSteps();
  return (
    <div className="rounded-2xl border border-[#E8E0CC] bg-white p-5" data-testid="launch-checklist">
      <p className="text-[10.5px] uppercase tracking-[0.22em] text-gold-dark font-bold">
        Your launch checklist
      </p>
      <p className="font-spectral text-lg text-ink mt-1">
        Step {step} of {LAUNCH_STEPS.length}: {LAUNCH_STEPS[step - 1].label}
      </p>
      <div className="mt-4 space-y-2">
        {LAUNCH_STEPS.map((s, i) => {
          const n = i + 1;
          const done = n < step;
          const current = n === step;
          return (
            <div
              key={s.label}
              className={`flex items-start gap-3 rounded-lg p-2.5 ${
                current ? "bg-[#FBF7EC] border border-[#E8D9A6]" : ""
              }`}
              data-testid={`launch-step-${n}`}
            >
              <div className={`w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 text-[11px] font-bold ${
                done ? "bg-emerald-100 text-emerald-700"
                  : current ? "bg-gold-gradient text-ink"
                  : "bg-gray-100 text-gray-400"
              }`}>
                {done ? "✓" : n}
              </div>
              <div className="flex-1 min-w-0">
                <p className={`text-[13px] font-semibold ${
                  done ? "text-emerald-700 line-through opacity-70"
                    : current ? "text-ink"
                    : "text-[#9A9A9A]"
                }`}>
                  {s.label}
                </p>
                {current && (
                  <p className="text-[12px] text-[#3a3a3a] mt-0.5 leading-relaxed">{s.tip}</p>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ---------------------------------------------------- Read-this-first banner
/**
 * Three-line money truth shown at the top of every pre-active stage
 * (upsell, Connect onboarding, slug-claim). Goal: every seller has
 * absorbed "3% to us + 2.9%+30¢ to Stripe = you keep ~93%" BEFORE they
 * waste time setting up Stripe. Dismissible per-browser; once
 * dismissed, it stays hidden so it doesn't nag the active dashboard.
 */
function FeeMathBanner() {
  const KEY = "armstrong-k9-storefront-fees-seen";
  const [open, setOpen] = useState(() => {
    try { return localStorage.getItem(KEY) !== "1"; } catch { return true; }
  });
  if (!open) return null;
  const dismiss = () => {
    try { localStorage.setItem(KEY, "1"); } catch { /* ignore */ }
    setOpen(false);
  };
  return (
    <div className="rounded-2xl border-2 border-gold-dark bg-[#FBF7EC] p-5 relative" data-testid="fee-math-banner">
      <button
        type="button"
        onClick={dismiss}
        aria-label="Dismiss"
        className="absolute top-3 right-3 text-[11px] text-[#6B6B6B] hover:text-ink uppercase tracking-wider font-semibold"
        data-testid="fee-math-dismiss"
      >
        Got it ✕
      </button>
      <p className="text-[10.5px] uppercase tracking-[0.22em] text-gold-dark font-bold">
        Read this first
      </p>
      <p className="font-spectral text-xl text-ink mt-1 leading-tight">
        Three things to know before you set up your Storefront
      </p>
      <ol className="mt-4 space-y-2.5 text-[13px] text-[#3a3a3a] leading-relaxed">
        <li className="flex gap-3">
          <span className="font-spectral font-bold text-gold-dark w-5 flex-shrink-0">1.</span>
          <span><strong className="text-ink">You&rsquo;re the seller of record.</strong> Every product, delivery, refund, and tax question is yours — Armstrong K9 Registry is the technology and payments facilitator, not the seller.</span>
        </li>
        <li className="flex gap-3">
          <span className="font-spectral font-bold text-gold-dark w-5 flex-shrink-0">2.</span>
          <span><strong className="text-ink">Fees come out automatically.</strong> Stripe takes <span className="font-tabular">2.9% + $0.30</span> per charge. We take <span className="font-tabular">3%</span> as our platform fee. Both are deducted from each sale before payout — you never get a separate invoice.</span>
        </li>
        <li className="flex gap-3">
          <span className="font-spectral font-bold text-gold-dark w-5 flex-shrink-0">3.</span>
          <span><strong className="text-ink">On a $40 sale: you net $37.34.</strong> Stripe pays out to your bank ~2 business days after each sale clears (first payout ~7 days while Stripe verifies your account).</span>
        </li>
      </ol>
      <p className="text-[11px] text-[#6B6B6B] mt-4">
        Full details:{" "}
        <a href="/storefront-seller-agreement" target="_blank" rel="noreferrer" className="underline">Seller Agreement</a>
        {" · "}
        <a href="/terms#kennel-storefronts" target="_blank" rel="noreferrer" className="underline">Terms §5b</a>
        {" · "}
        <a href="/help/stripe-tax-setup" target="_blank" rel="noreferrer" className="underline font-semibold text-ink" data-testid="mystore-tax-setup-link">Stripe Tax setup →</a>
      </p>
    </div>
  );
}

// ---------------------------------------------------- Stage 1: upsell
function UpsellCard({ onSubscribe, fee }) {
  const { prices } = usePublicPrices();
  const monthly = prices("storefront_monthly", "$19");
  const annual = prices("storefront_annual", "$190");
  return (
    <div className="rounded-2xl border border-[#E8D9A6] bg-gradient-to-br from-[#FBF7EC] to-white p-8">
      <Storefront size={42} weight="duotone" className="text-gold-dark mb-4" />
      <h1 className="font-spectral text-3xl text-ink">Open your Dog Shop</h1>
      <p className="text-[#3a3a3a] mt-2 max-w-xl leading-relaxed">
        Sell dog food (local delivery) and merch under your own brand — your photos,
        your prices, your customer list. Payouts go straight to your Stripe account.
        We take <strong>{Math.round((fee || 0.03) * 100)}%</strong> of each sale to keep the lights on.
      </p>
      <ul className="mt-5 space-y-2 text-[14px] text-[#3a3a3a]">
        {[
          "Your own /store/your-kennel-name page",
          "Add products with photos + prices in minutes",
          "Local delivery zones — restrict to ZIPs near you",
          "Direct payouts via Stripe Connect (you keep customer relationship)",
          "Cancel any time, no prorated refunds",
        ].map((line) => (
          <li key={line} className="flex items-start gap-2">
            <CheckCircle size={18} weight="duotone" className="text-gold flex-shrink-0 mt-0.5" />
            {line}
          </li>
        ))}
      </ul>
      <Button
        onClick={onSubscribe}
        className="mt-6 rounded-full bg-ink hover:bg-crimson text-white h-11 px-6"
        data-testid="storefront-subscribe-cta"
      >
        Subscribe — {monthly}/mo →
      </Button>
      <p className="text-[11px] text-[#9A9A9A] mt-3">
        Or save with the annual plan ({annual}/yr) on the billing page.
      </p>
    </div>
  );
}

// ---------------------------------------------------- Stage 2: Connect
function ConnectNudge({ onGo }) {
  return (
    <div className="space-y-5">
      <div className="rounded-2xl border border-amber-200 bg-amber-50/60 p-6">
        <Warning size={32} weight="duotone" className="text-amber-700 mb-3" />
        <h2 className="font-spectral text-2xl text-ink">One more step: connect your Stripe account</h2>
        <p className="text-[14px] text-[#3a3a3a] mt-2 leading-relaxed">
          Customer payments need somewhere to go. Stripe Connect routes each sale
          straight to your bank account — we never hold your money.
        </p>
        <Button onClick={onGo} className="mt-4 rounded-full bg-ink hover:bg-crimson text-white" data-testid="connect-onboard-cta">
          Set up payouts →
        </Button>
      </div>
      <StripeHowItWorks />
    </div>
  );
}

/**
 * "How Stripe works for your storefront" — single source of truth for
 * the getting-started copy. Rendered on the Connect-onboarding stage
 * AND inside the active dashboard so sellers can reference it any time.
 * Covers everything new sellers ask in their first 48 hours: what they
 * need to onboard, who collects sales tax, when they get paid, how
 * refunds/disputes work, and where to get help.
 */
function StripeHowItWorks() {
  return (
    <details className="rounded-2xl border border-[#E8E0CC] bg-white p-5" data-testid="stripe-help" open>
      <summary className="cursor-pointer font-spectral text-lg text-ink select-none">
        How Stripe payouts work (read this before onboarding)
      </summary>
      <div className="mt-4 space-y-4 text-[13px] text-[#3a3a3a] leading-relaxed">
        <div>
          <p className="font-semibold text-ink">What you&rsquo;ll need to onboard</p>
          <ul className="mt-1 space-y-1 list-disc pl-5">
            <li><strong>Legal name + date of birth</strong> (matches your government ID)</li>
            <li><strong>Last 4 of SSN</strong> if sole proprietor, OR your <strong>EIN</strong> if registered as a business</li>
            <li><strong>Photo ID</strong> (driver&rsquo;s license or passport — uploaded directly to Stripe, never to us)</li>
            <li><strong>Bank account routing + account number</strong> for payouts</li>
            <li>Takes ~3–5 minutes. You can save and come back if you don&rsquo;t have everything handy.</li>
          </ul>
        </div>
        <div>
          <p className="font-semibold text-ink">When you get paid</p>
          <p>By default Stripe pays out to your bank <strong>2 business days after</strong> each charge clears. You can change the schedule in your Stripe dashboard (daily, weekly, or monthly). The first payout takes ~7 days while Stripe verifies your bank.</p>
        </div>
        <div>
          <p className="font-semibold text-ink">Fees (in plain English)</p>
          <p>On a $40 sale: Stripe takes <strong>$1.46</strong> (2.9% + $0.30), we take <strong>$2.00</strong> (5% platform fee), you net <strong>$36.54</strong>. Both fees come out automatically — you never see a separate invoice.</p>
        </div>
        <div>
          <p className="font-semibold text-ink">Sales tax</p>
          <p>You&rsquo;re the seller of record — you&rsquo;re responsible for collecting and remitting sales tax in any state where you have nexus. Most dog food is tax-exempt in many states (check yours). We can wire Stripe Tax onto your storefront on request to handle this automatically (small extra Stripe fee).</p>
        </div>
        <div>
          <p className="font-semibold text-ink">Refunds &amp; disputes</p>
          <p>Refunds come out of your Stripe balance — issue them from your Stripe dashboard or message us. Disputes (chargebacks) are handled directly between you and Stripe; we don&rsquo;t mediate but we can help you draft the response.</p>
        </div>
        <div>
          <p className="font-semibold text-ink">Customer support</p>
          <p>Your storefront page lists your contact email. Buyers are told upfront they&rsquo;re buying from you, not us — questions about delivery, products, or refunds go to you directly.</p>
        </div>
        <div className="pt-2 text-[12px] text-[#6B6B6B]">
          Questions before you onboard? Email <a href="mailto:hello@armstrongk9registry.com" className="underline">hello@armstrongk9registry.com</a>.
        </div>
      </div>
    </details>
  );
}

// ---------------------------------------------------- Stage 3: slug claim
function ClaimSlugForm({ onCreated }) {
  const [form, setForm] = useState({ name: "", slug: "", bio: "" });
  const [accepted, setAccepted] = useState(false);
  const [busy, setBusy] = useState(false);

  const onSlugSuggest = () => {
    if (form.slug || !form.name) return;
    const suggested = form.name.toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-|-$/g, "")
      .slice(0, 40);
    if (suggested) setForm((f) => ({ ...f, slug: suggested }));
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!accepted) { toast.error("Please review and accept the Storefront Seller Agreement first."); return; }
    setBusy(true);
    try {
      await api.post("/me/store", { ...form, accepted_seller_agreement: true });
      toast.success("Store created!");
      onCreated();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to create store");
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit} className="rounded-2xl border border-[#E8E0CC] bg-white p-6" data-testid="claim-slug-form">
      <h1 className="font-spectral text-2xl text-ink">Claim your storefront URL</h1>
      <p className="text-[#6B6B6B] text-sm mt-1">This is the public link you&rsquo;ll share with customers.</p>

      <div className="mt-5 space-y-4">
        <div>
          <Label className="text-xs">Store name</Label>
          <Input
            required
            value={form.name}
            onBlur={onSlugSuggest}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            placeholder="e.g. Granite Peak K9"
            data-testid="store-name-input"
          />
        </div>
        <div>
          <Label className="text-xs">URL slug</Label>
          <div className="flex items-center gap-2 mt-1">
            <span className="text-[12px] text-[#9A9A9A] font-tabular">{webOrigin()}/store/</span>
            <Input
              required
              value={form.slug}
              onChange={(e) => setForm({ ...form, slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, "") })}
              placeholder="granite-peak"
              className="flex-1"
              data-testid="store-slug-input"
            />
          </div>
          <p className="text-[11px] text-[#9A9A9A] mt-1">Lowercase letters, numbers, and dashes only. 3–40 chars.</p>
        </div>
        <div>
          <Label className="text-xs">Short bio (optional)</Label>
          <Textarea
            value={form.bio}
            onChange={(e) => setForm({ ...form, bio: e.target.value })}
            placeholder="What makes your storefront worth a customer's trust?"
            rows={3}
          />
        </div>
      </div>

      <div className="mt-5 rounded-lg border border-[#E8D9A6] bg-[#FBF7EC]/50 p-3 flex items-start gap-2">
        <input
          id="accept-seller"
          type="checkbox"
          checked={accepted}
          onChange={(e) => setAccepted(e.target.checked)}
          className="mt-1"
          data-testid="seller-agreement-checkbox"
        />
        <label htmlFor="accept-seller" className="text-[12px] text-[#3a3a3a] leading-relaxed cursor-pointer">
          I have read and agree to the{" "}
          <a href="/storefront-seller-agreement" target="_blank" rel="noreferrer" className="underline text-gold-dark font-semibold">
            Storefront Seller Agreement
          </a>,{" "}
          <a href="/terms" target="_blank" rel="noreferrer" className="underline">Terms of Service</a>, and
          Stripe&rsquo;s
          {" "}<a href="https://stripe.com/restricted-businesses" target="_blank" rel="noreferrer" className="underline">Restricted Businesses</a>{" "}
          policy. I am the merchant of record for all sales on my Storefront.
        </label>
      </div>

      <Button
        type="submit"
        disabled={busy || !form.name || !form.slug || !accepted}
        className="mt-5 rounded-full bg-ink hover:bg-crimson text-white w-full h-11 disabled:opacity-50"
        data-testid="store-create-btn"
      >
        {busy ? "Creating…" : "Create my storefront →"}
      </Button>
    </form>
  );
}

// ---------------------------------------------------- Active store: header
function StoreHeader({ store, platformFeePct, isFeatured, featuredUntil }) {
  const url = `${webOrigin()}/store/${store.slug}`;
  // On mobile (iOS / Android), prefer the native share sheet so users
  // can drop the link straight into iMessage / WhatsApp / Mail. Falls
  // back to clipboard on desktop or older browsers.
  const copy = async () => {
    if (typeof navigator !== "undefined" && navigator.share) {
      try {
        await navigator.share({ title: store.name || "My store", url });
        return;
      } catch (e) {
        if (e?.name === "AbortError") return; // user cancelled
      }
    }
    navigator.clipboard.writeText(url);
    toast.success("Link copied");
  };
  return (
    <div className="rounded-2xl border border-[#E8D9A6] bg-[#FBF7EC]/40 p-5 flex items-center gap-4 flex-wrap" data-testid="store-header">
      <Storefront size={32} weight="duotone" className="text-gold-dark" />
      <div className="flex-1 min-w-[200px]">
        <div className="flex items-center gap-2 flex-wrap">
          <h1 className="font-spectral text-2xl text-ink">{store.name}</h1>
          {isFeatured && (
            <Badge className="bg-gold-gradient text-ink border-0 font-bold text-[10px] tracking-[0.15em] uppercase" data-testid="featured-badge">
              ★ Featured
            </Badge>
          )}
        </div>
        <button onClick={copy} className="text-[12.5px] text-[#6B6B6B] hover:text-gold-dark underline underline-offset-2 font-tabular" data-testid="copy-store-link">
          {url}
        </button>
        {isFeatured && featuredUntil && (
          <p className="text-[11px] text-gold-dark mt-1">
            Featured until {new Date(featuredUntil).toLocaleDateString()}
          </p>
        )}
      </div>
      <div className="flex items-center gap-3 text-[11px] text-[#6B6B6B]">
        <Badge variant="outline" className="bg-white border-emerald-300 text-emerald-800">
          <CheckCircle size={11} weight="fill" className="mr-1" /> Live
        </Badge>
        <span>Platform fee: {Math.round((platformFeePct || 0.03) * 100)}%</span>
      </div>
      <a href={`/store/${store.slug}`} target="_blank" rel="noreferrer" className="text-[12px] font-semibold text-ink hover:text-crimson underline underline-offset-4">
        View public storefront →
      </a>
    </div>
  );
}

// ---------------------------------------------------- Products tab
function ProductsTab() {
  const [products, setProducts] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const reload = async () => {
    const { data } = await api.get("/me/store/products");
    setProducts(data.products || []);
  };
  useEffect(() => { reload(); }, []);

  const remove = async (id) => {
    if (!window.confirm("Delete this product?")) return;
    await api.delete(`/me/store/products/${id}`);
    toast.success("Deleted");
    reload();
  };

  return (
    <div className="space-y-4 py-6">
      <div className="flex items-end justify-between">
        <h2 className="font-spectral text-xl">Catalog</h2>
        <Button onClick={() => { setEditing(null); setDialogOpen(true); }} className="rounded-full bg-ink hover:bg-crimson text-white" data-testid="product-create-btn">
          <Plus size={16} className="mr-1" /> New product
        </Button>
      </div>
      <p className="text-[12px] text-[#6B6B6B]">Tap a card to edit. Toggle the stock badge to hide/show.</p>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {products.length === 0 && <p className="text-sm text-[#6B6B6B]">No products yet — add your first.</p>}
        {products.map((p) => (
          <ProductCard key={p.id} product={p} onChanged={reload} onEdit={() => { setEditing(p); setDialogOpen(true); }} onDelete={() => remove(p.id)} />
        ))}
      </div>
      <ProductDialog open={dialogOpen} editing={editing} onClose={() => setDialogOpen(false)} onSaved={() => { setDialogOpen(false); reload(); }} />
    </div>
  );
}

function ProductCard({ product, onChanged, onEdit, onDelete }) {
  const photoSrc = product.photo_url ? (thumbUrl(product.photo_url) || product.photo_url) : null;
  const patch = async (updates) => {
    try { await api.patch(`/me/store/products/${product.id}`, updates); onChanged(); }
    catch { toast.error("Save failed"); }
  };
  return (
    <div className="rounded-xl border border-[#E8E0CC] bg-white overflow-hidden flex flex-col" data-testid={`product-card-${product.id}`}>
      {photoSrc ? (
        <img src={photoSrc} alt={product.name} className="w-full h-40 object-cover" />
      ) : (
        <div className="w-full h-40 bg-cream flex items-center justify-center text-[#9A9A9A]">
          <Camera size={28} weight="duotone" />
        </div>
      )}
      <div className="p-3 flex-1 flex flex-col">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <h3 className="font-spectral text-base leading-tight truncate">{product.name}</h3>
            <p className="text-[11px] text-[#6B6B6B] uppercase tracking-wider mt-0.5">{product.kind}{product.bag_size ? ` · ${product.bag_size}` : ""}</p>
          </div>
          <span className="font-tabular text-base text-ink">${(Number(product.price_usd) || 0).toFixed(2)}</span>
        </div>
        <div className="mt-3 flex items-center justify-between text-xs">
          <Badge
            variant={product.in_stock ? "default" : "secondary"}
            className={`cursor-pointer ${product.in_stock ? "bg-emerald-100 text-emerald-800 hover:bg-emerald-200" : "hover:bg-gray-200"}`}
            onClick={() => patch({ in_stock: !product.in_stock })}
          >
            {product.in_stock ? "In stock" : "Hidden"}
          </Badge>
          <div className="flex gap-1">
            <Button size="sm" variant="ghost" onClick={onEdit} className="h-7 text-[12px]">Edit</Button>
            <Button size="sm" variant="ghost" onClick={onDelete} className="h-7 text-[12px] text-crimson"><Trash size={12} /></Button>
          </div>
        </div>
      </div>
    </div>
  );
}

function ProductDialog({ open, editing, onClose, onSaved }) {
  const EMPTY = { kind: "food", name: "", description: "", price_usd: "", photo_url: "", bag_size: "", in_stock: true, ships_nationwide: false, personalizable: false, customization_instructions: "", consumable: false, reorder_days: 30 };
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    if (open) setForm(editing ? { ...EMPTY, ...editing, reorder_days: editing.reorder_days || 30 } : EMPTY);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, editing]);

  const uploadPhoto = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const path = r.data.storage_path;
      if (path) setForm((f) => ({ ...f, photo_url: path }));
    } catch (err) {
      toast.error("Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const payload = { ...form, price_usd: parseFloat(form.price_usd), reorder_days: form.consumable ? (parseInt(form.reorder_days, 10) || 30) : null };
      if (editing) {
        await api.patch(`/me/store/products/${editing.id}`, payload);
        toast.success("Updated");
      } else {
        await api.post("/me/store/products", payload);
        toast.success("Created");
      }
      onSaved();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    } finally { setBusy(false); }
  };

  const photoSrc = form.photo_url ? (thumbUrl(form.photo_url) || form.photo_url) : null;

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="product-dialog">
        <DialogHeader>
          <DialogTitle>{editing ? "Edit product" : "New product"}</DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <div>
            <Label className="text-xs">Kind</Label>
            <div className="flex gap-2">
              {["food", "merch"].map((k) => (
                <button
                  key={k}
                  type="button"
                  onClick={() => setForm({ ...form, kind: k })}
                  className={`rounded-full px-4 py-1.5 text-[12px] border ${form.kind === k ? "bg-ink text-white border-ink" : "bg-white text-[#3a3a3a] border-[#E5E5E5]"}`}
                  data-testid={`kind-${k}`}
                >
                  {k === "food" ? "Dog food / consumables" : "Merch / apparel"}
                </button>
              ))}
            </div>
          </div>
          <div>
            <Label className="text-xs">Name</Label>
            <Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Price (USD)</Label>
              <Input required type="number" step="0.01" min="0.01" value={form.price_usd} onChange={(e) => setForm({ ...form, price_usd: e.target.value })} />
            </div>
            <div>
              <Label className="text-xs">{form.kind === "food" ? "Bag size" : "Size / variant"}</Label>
              <Input value={form.bag_size || ""} onChange={(e) => setForm({ ...form, bag_size: e.target.value })} placeholder={form.kind === "food" ? "30 lb bag" : "L, XL, …"} />
            </div>
          </div>
          <div>
            <Label className="text-xs">Photo</Label>
            <div className="rounded-lg border border-dashed border-[#E8E0CC] bg-cream p-2">
              {photoSrc ? (
                <div className="flex items-center gap-2">
                  <img src={photoSrc} alt="" className="w-16 h-16 rounded object-cover" />
                  <Button type="button" variant="outline" size="sm" onClick={() => setForm({ ...form, photo_url: "" })} className="text-xs">Remove</Button>
                </div>
              ) : (
                <label className="block text-center cursor-pointer text-[12px] text-[#6B6B6B] hover:text-ink py-3">
                  {uploading ? "Uploading…" : "Click to upload photo"}
                  <input type="file" accept="image/*" className="hidden" onChange={uploadPhoto} />
                </label>
              )}
            </div>
          </div>
          <div>
            <Label className="text-xs">Description</Label>
            <Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={2} />
          </div>
          {form.kind === "merch" && (
            <div className="flex items-center gap-2">
              <input id="ships" type="checkbox" checked={!!form.ships_nationwide} onChange={(e) => setForm({ ...form, ships_nationwide: e.target.checked })} />
              <Label htmlFor="ships" className="text-sm">Ships nationwide (skips local delivery zone check)</Label>
            </div>
          )}
          <div className="rounded-lg border border-[#E8D9A6] bg-[#FBF7EC]/50 p-3 space-y-2">
            <div className="flex items-center gap-2">
              <input
                id="personalizable"
                type="checkbox"
                checked={!!form.personalizable}
                onChange={(e) => setForm({ ...form, personalizable: e.target.checked })}
                data-testid="product-personalizable"
              />
              <Label htmlFor="personalizable" className="text-sm font-semibold">
                This item is personalizable
              </Label>
            </div>
            <p className="text-[11px] text-[#6B6B6B] leading-relaxed">
              Buyers see a gold &ldquo;Personalize&rdquo; badge. Instead of buying directly, they fill a form and email you for a mockup &amp; price.
            </p>
            {form.personalizable && (
              <div>
                <Label className="text-[11px]">Customization instructions (optional)</Label>
                <Textarea
                  rows={2}
                  value={form.customization_instructions || ""}
                  onChange={(e) => setForm({ ...form, customization_instructions: e.target.value })}
                  placeholder="e.g. Tell me your dog's name + a photo. Allow 5 business days for mockup."
                  className="bg-white text-[12px]"
                  data-testid="product-customization-instructions"
                />
              </div>
            )}
          </div>
          <div className="rounded-lg border border-[#D9E8D9] bg-[#F3FAF3]/60 p-3 space-y-2">
            <div className="flex items-center gap-2">
              <input
                id="consumable"
                type="checkbox"
                checked={!!form.consumable}
                onChange={(e) => setForm({ ...form, consumable: e.target.checked })}
                data-testid="product-consumable"
              />
              <Label htmlFor="consumable" className="text-sm font-semibold">
                Consumable (treats, food, shampoo…)
              </Label>
            </div>
            <p className="text-[11px] text-[#6B6B6B] leading-relaxed">
              Buyers get a friendly &ldquo;running low?&rdquo; push with a one-tap reorder link when it&rsquo;s about time to restock.
            </p>
            {form.consumable && (
              <div className="flex items-center gap-2">
                <Label className="text-[11px] whitespace-nowrap">Nudge after</Label>
                <Input
                  type="number"
                  min="7"
                  max="365"
                  className="w-24 bg-white text-[13px]"
                  value={form.reorder_days}
                  onChange={(e) => setForm({ ...form, reorder_days: e.target.value })}
                  data-testid="product-reorder-days"
                />
                <span className="text-[11px] text-[#6B6B6B]">days (e.g. 30 for a monthly bag)</span>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button type="submit" disabled={busy} className="rounded-full bg-gold-gradient text-ink font-semibold w-full">
              {busy ? "Saving…" : editing ? "Save" : "Create"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------- Orders tab
function OrdersTab() {
  const [orders, setOrders] = useState([]);
  const [pushing, setPushing] = useState(null);
  // Banner state: shown when at least one order has "Auto-fulfillment
  // is OFF" stamped on it, AND the seller hasn't dismissed it yet.
  // localStorage key keeps the dismissal sticky across sessions.
  const BANNER_KEY = "armstrong.autofulfill.banner.dismissed";
  const [bannerDismissed, setBannerDismissed] = useState(() => {
    try {
      return localStorage.getItem(BANNER_KEY) === "1";
    } catch {
      return false;
    }
  });
  const [enablingBanner, setEnablingBanner] = useState(false);
  useEffect(() => { api.get("/me/store/orders").then((r) => setOrders(r.data.orders || [])); }, []);

  const ordersWithOffReason = orders.filter(
    (o) =>
      o.kind === "merch" &&
      !o.printify_order_id &&
      typeof o.auto_fulfill_skipped_reason === "string" &&
      /Auto-fulfillment is OFF/i.test(o.auto_fulfill_skipped_reason)
  );
  const showAutoFulfillBanner =
    !bannerDismissed && ordersWithOffReason.length > 0;

  const enableAutoFulfill = async () => {
    setEnablingBanner(true);
    try {
      await api.post("/me/store/printify/auto-fulfill", { enabled: true });
      toast.success(
        ordersWithOffReason.length === 1
          ? "Auto-fulfill turned on — push this order with Send to Printify below."
          : `Auto-fulfill turned on — push the ${ordersWithOffReason.length} waiting orders below.`
      );
      // Clear the off-reason from local state so the banner + amber
      // chips disappear immediately, without a refetch round-trip.
      setOrders((cur) =>
        cur.map((o) =>
          o.auto_fulfill_skipped_reason &&
          /Auto-fulfillment is OFF/i.test(o.auto_fulfill_skipped_reason)
            ? { ...o, auto_fulfill_skipped_reason: null }
            : o
        )
      );
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't enable auto-fulfill");
    } finally {
      setEnablingBanner(false);
    }
  };

  const dismissBanner = () => {
    try {
      localStorage.setItem(BANNER_KEY, "1");
    } catch { /* ignore quota / private-mode */ }
    setBannerDismissed(true);
  };

  const markDelivered = async (id) => {
    await api.post(`/me/store/orders/${id}/mark-delivered`);
    toast.success("Marked delivered");
    setOrders((cur) => cur.map((o) => o.id === id ? { ...o, status: "delivered" } : o));
  };

  const sendToPrintify = async (id) => {
    setPushing(id);
    try {
      const { data } = await api.post(`/me/store/printify/orders/${id}/fulfill`);
      toast.success(data?.already_fulfilled ? "Already pushed to Printify" : "Sent to Printify");
      setOrders((cur) => cur.map((o) => o.id === id ? {
        ...o,
        printify_order_id: data.printify_order_id,
        fulfillment_status: "queued",
        fulfillment_error: null,
      } : o));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Send failed");
    } finally {
      setPushing(null);
    }
  };

  return (
    <div className="py-6">
      {showAutoFulfillBanner && (
        <div
          data-testid="autofulfill-prompt-banner"
          className="mb-4 rounded-xl border border-amber-200 bg-gradient-to-r from-amber-50 to-amber-50/50 p-4 sm:p-5 flex flex-col sm:flex-row sm:items-center gap-3"
        >
          <div className="flex-shrink-0 w-9 h-9 rounded-full bg-amber-100 text-amber-700 flex items-center justify-center font-bold">
            ⚡
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-[13px] font-semibold text-ink">
              {ordersWithOffReason.length === 1
                ? "1 Printify order is waiting — auto-fulfill is off"
                : `${ordersWithOffReason.length} Printify orders are waiting — auto-fulfill is off`}
            </p>
            <p className="text-[12px] text-[#5C5C5C] mt-0.5 leading-snug">
              Turn it on once and every future paid Printify order ships
              automatically. The {ordersWithOffReason.length === 1 ? "order below still needs" : "orders below still need"}{" "}
              a one-tap manual push.
            </p>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            <Button
              onClick={enableAutoFulfill}
              disabled={enablingBanner}
              data-testid="autofulfill-prompt-enable"
              className="rounded-full bg-ink text-white hover:bg-[#2A2A2A] font-semibold"
            >
              {enablingBanner ? "Turning on…" : "Turn on auto-fulfill"}
            </Button>
            <button
              type="button"
              onClick={dismissBanner}
              data-testid="autofulfill-prompt-dismiss"
              aria-label="Dismiss"
              className="text-[#9A9A9A] hover:text-ink text-xs underline underline-offset-2"
            >
              Not now
            </button>
          </div>
        </div>
      )}
      <div className="rounded-xl border border-[#E8E0CC] bg-white overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-[#FAFAFA] text-[11px] uppercase tracking-wider text-[#6B6B6B]">
            <tr>
              <th className="text-left px-3 py-2">When</th>
              <th className="text-left px-3 py-2">Customer</th>
              <th className="text-left px-3 py-2">Product</th>
              <th className="text-left px-3 py-2">Qty</th>
              <th className="text-right px-3 py-2">Total</th>
              <th className="text-left px-3 py-2">Delivery</th>
              <th className="text-left px-3 py-2">Status</th>
              <th className="text-right px-3 py-2"></th>
            </tr>
          </thead>
          <tbody>
            {orders.length === 0 && <tr><td colSpan={8} className="py-6 text-center text-[#6B6B6B]">No orders yet.</td></tr>}
            {orders.map((o) => (
              <tr key={o.id} className="border-t border-[#F1ECDD]">
                <td className="px-3 py-2 text-[12px] text-[#6B6B6B]">{new Date(o.created_at).toLocaleDateString()}</td>
                <td className="px-3 py-2">
                  <div className="font-medium">{o.customer_name}</div>
                  <div className="text-[11px] text-[#6B6B6B]">{o.customer_email} · {o.customer_phone}</div>
                </td>
                <td className="px-3 py-2">{o.product_name}</td>
                <td className="px-3 py-2 font-tabular">{o.quantity}</td>
                <td className="px-3 py-2 text-right font-tabular">${(o.amount_cents / 100).toFixed(2)}</td>
                <td className="px-3 py-2 text-[12px]"><div>{o.delivery_address}</div><div className="text-[11px] text-[#6B6B6B]">{o.delivery_zip}</div></td>
                <td className="px-3 py-2">
                  <StatusPill status={o.status} />
                  {o.kind === "merch" && o.printify_order_id && (
                    <div className="text-[10px] text-emerald-700 mt-1" data-testid={`order-printify-id-${o.id}`}>
                      Printify #{o.printify_order_id.slice(-8)}
                    </div>
                  )}
                  {o.kind === "merch" && o.fulfillment_status === "error" && (
                    <div className="text-[10px] text-crimson mt-1 max-w-[200px] truncate" title={o.fulfillment_error} data-testid={`order-printify-error-${o.id}`}>
                      Printify error: {o.fulfillment_error}
                    </div>
                  )}
                  {/* Surface WHY auto-fulfill skipped — Lauren-bug fix
                      (2026-06). Previously the function silently
                      returned, leaving sellers wondering if Printify
                      was broken. Now they see exactly which condition
                      failed and the manual "Send to Printify" button
                      is one tap below. */}
                  {o.kind === "merch" &&
                    !o.printify_order_id &&
                    o.auto_fulfill_skipped_reason && (
                      <div
                        className="text-[10px] text-amber-700 bg-amber-50 border border-amber-200 rounded px-1.5 py-1 mt-1 max-w-[220px] leading-snug"
                        data-testid={`order-printify-skip-reason-${o.id}`}
                      >
                        <strong className="font-semibold">Auto-fulfill skipped:</strong>{" "}
                        {o.auto_fulfill_skipped_reason}
                      </div>
                    )}
                </td>
                <td className="px-3 py-2 text-right space-y-1">
                  {o.status === "paid" && o.kind === "merch" && !o.printify_order_id && (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => sendToPrintify(o.id)}
                      disabled={pushing === o.id}
                      className="rounded-full h-7 text-[11px] block w-full"
                      data-testid={`order-printify-push-${o.id}`}
                    >
                      {pushing === o.id ? "Sending…" : "Send to Printify"}
                    </Button>
                  )}
                  {o.status === "paid" && (
                    <Button size="sm" variant="outline" onClick={() => markDelivered(o.id)} className="rounded-full h-7 text-[11px] block w-full">Mark delivered</Button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function StatusPill({ status }) {
  const map = {
    pending_payment: { label: "Pending", cls: "bg-amber-100 text-amber-800" },
    paid: { label: "Paid · ready to deliver", cls: "bg-emerald-100 text-emerald-800" },
    delivered: { label: "Delivered", cls: "bg-gray-200 text-gray-700" },
  };
  const v = map[status] || { label: status, cls: "bg-gray-100 text-gray-700" };
  return <span className={`text-[10px] uppercase tracking-wider font-bold rounded-full px-2 py-0.5 ${v.cls}`}>{v.label}</span>;
}

// ---------------------------------------------------- Settings tab
function SettingsTab({ store, onSaved }) {
  const [form, setForm] = useState({
    name: store.name,
    bio: store.bio || "",
    contact_email: store.contact_email || "",
    food_allowed_zips: (store.food_allowed_zips || []).join(", "),
    food_exception_emails: (store.food_exception_emails || []).join(", "),
    food_zone_help_text: store.food_zone_help_text || "",
  });
  const [busy, setBusy] = useState(false);
  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.patch("/me/store", form);
      toast.success("Saved");
      onSaved();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    } finally { setBusy(false); }
  };

  return (
    <form onSubmit={save} className="py-6 space-y-5 max-w-3xl">
      <section>
        <h2 className="font-spectral text-xl mb-3">Storefront identity</h2>
        <div className="grid md:grid-cols-2 gap-3">
          <div>
            <Label className="text-xs">Store name</Label>
            <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required />
          </div>
          <div>
            <Label className="text-xs">Contact email</Label>
            <Input type="email" value={form.contact_email} onChange={(e) => setForm({ ...form, contact_email: e.target.value })} />
          </div>
        </div>
        <div className="mt-3">
          <Label className="text-xs">Short bio</Label>
          <Textarea value={form.bio} onChange={(e) => setForm({ ...form, bio: e.target.value })} rows={3} />
        </div>
      </section>

      <section className="rounded-2xl border border-[#E8D9A6] bg-[#FBF7EC]/60 p-4">
        <h2 className="font-spectral text-xl">Local delivery zone</h2>
        <p className="text-[12.5px] text-[#6B6B6B] mt-1">
          Customers outside these ZIPs can&rsquo;t order food. Leave blank to allow everyone.
        </p>
        <div className="grid md:grid-cols-2 gap-3 mt-3">
          <div>
            <Label className="text-xs">Allowed ZIP codes</Label>
            <Textarea
              value={form.food_allowed_zips}
              onChange={(e) => setForm({ ...form, food_allowed_zips: e.target.value })}
              rows={3}
              placeholder="19103, 19104, 19106…"
              className="bg-white font-tabular text-[13px]"
              data-testid="zone-zips"
            />
          </div>
          <div>
            <Label className="text-xs">Exception emails</Label>
            <Textarea
              value={form.food_exception_emails}
              onChange={(e) => setForm({ ...form, food_exception_emails: e.target.value })}
              rows={3}
              placeholder="family@example.com"
              className="bg-white text-[13px]"
              data-testid="zone-emails"
            />
          </div>
        </div>
        <div className="mt-3">
          <Label className="text-xs">Out-of-zone message (optional)</Label>
          <Input
            value={form.food_zone_help_text}
            onChange={(e) => setForm({ ...form, food_zone_help_text: e.target.value })}
            placeholder="We deliver Tue/Thu within 45 min of West Philly."
            className="bg-white"
          />
        </div>
      </section>

      <Button type="submit" disabled={busy} className="rounded-full bg-ink hover:bg-crimson text-white h-10 px-6" data-testid="settings-save">
        {busy ? "Saving…" : "Save changes"}
      </Button>
    </form>
  );
}

// ---------------------------------------------------- Branding tab
const ACCENT_OPTIONS = [
  { value: "gold", label: "Gold (default)", hex: "#C9A227" },
  { value: "ink", label: "Ink black", hex: "#0E0E0E" },
  { value: "crimson", label: "Crimson", hex: "#C1272D" },
  { value: "emerald", label: "Emerald", hex: "#0E7A53" },
  { value: "sky", label: "Sky", hex: "#1E6FB6" },
  { value: "violet", label: "Violet", hex: "#6B3FA0" },
];

function BrandingTab({ store, onSaved }) {
  const [accent, setAccent] = useState(store.accent_color || "gold");
  const [heroUrl, setHeroUrl] = useState(store.hero_image || "");
  const [uploading, setUploading] = useState(false);
  const [busy, setBusy] = useState(false);

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const path = r.data.storage_path;
      if (path) setHeroUrl(path);
    } catch { toast.error("Upload failed"); }
    finally { setUploading(false); }
  };

  const save = async () => {
    setBusy(true);
    try {
      await api.patch("/me/store", { hero_image: heroUrl, accent_color: accent });
      toast.success("Branding saved");
      onSaved();
    } catch (err) { toast.error(err?.response?.data?.detail || "Save failed"); }
    finally { setBusy(false); }
  };

  const heroSrc = heroUrl ? (thumbUrl(heroUrl) || heroUrl) : null;

  return (
    <div className="py-6 space-y-6 max-w-3xl" data-testid="branding-tab">
      <section>
        <h2 className="font-spectral text-xl">Hero image</h2>
        <p className="text-[12.5px] text-[#6B6B6B] mt-1">Shown at the top of your public storefront and on your directory card. Wide landscape works best (1600 × 900 or similar).</p>
        <div className="mt-3 rounded-2xl border border-dashed border-[#E8E0CC] bg-cream overflow-hidden">
          {heroSrc ? (
            <div>
              <img src={heroSrc} alt="" className="w-full h-56 object-cover" />
              <div className="p-3 flex gap-2 bg-white border-t border-[#E8E0CC]">
                <label className="cursor-pointer">
                  <input type="file" accept="image/*" className="hidden" onChange={upload} />
                  <span className="rounded-full border border-[#E5E5E5] px-3 py-1.5 text-[12px] hover:bg-cream">{uploading ? "Uploading…" : "Replace"}</span>
                </label>
                <Button type="button" size="sm" variant="ghost" onClick={() => setHeroUrl("")} className="text-crimson text-xs">Remove</Button>
              </div>
            </div>
          ) : (
            <label className="cursor-pointer flex flex-col items-center justify-center py-12 gap-2 text-[#6B6B6B] hover:bg-[#FBF7EC]">
              <Camera size={28} weight="duotone" className="text-gold-dark" />
              <span className="text-[12px] font-medium">{uploading ? "Uploading…" : "Click to upload hero image"}</span>
              <span className="text-[10px] text-[#9A9A9A]">JPG / PNG / WEBP — up to 10MB</span>
              <input type="file" accept="image/*" className="hidden" onChange={upload} />
            </label>
          )}
        </div>
      </section>

      <section>
        <h2 className="font-spectral text-xl">Accent color</h2>
        <p className="text-[12.5px] text-[#6B6B6B] mt-1">Used on buttons, badges, and dividers across your public storefront.</p>
        <div className="mt-3 flex flex-wrap gap-2">
          {ACCENT_OPTIONS.map((opt) => (
            <button
              key={opt.value}
              type="button"
              onClick={() => setAccent(opt.value)}
              className={`flex items-center gap-2 px-3 py-2 rounded-full border text-[12px] ${
                accent === opt.value ? "border-ink bg-ink text-white" : "border-[#E5E5E5] bg-white text-ink hover:border-ink"
              }`}
              data-testid={`accent-${opt.value}`}
            >
              <span className="w-3 h-3 rounded-full" style={{ background: opt.hex }} />
              {opt.label}
            </button>
          ))}
        </div>
      </section>

      <Button onClick={save} disabled={busy} className="rounded-full bg-ink hover:bg-crimson text-white h-10 px-6" data-testid="branding-save">
        {busy ? "Saving…" : "Save branding"}
      </Button>
    </div>
  );
}

// ---------------------------------------------------- Featured tab
function FeaturedTab({ isFeatured, featuredUntil }) {
  const [busy, setBusy] = useState(false);
  const buy = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/billing/checkout", {
        package_id: "storefront_featured_monthly",
        origin_url: webOrigin(),
      });
      if (data?.url) window.location.href = data.url;
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Checkout failed");
      setBusy(false);
    }
  };
  return (
    <div className="py-6 max-w-2xl" data-testid="featured-tab">
      <div className="rounded-2xl border border-[#E8D9A6] bg-gradient-to-br from-[#FBF7EC] to-white p-6">
        <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.25em] text-gold mb-3">
          ★ Featured rotation
        </div>
        <h2 className="font-spectral text-2xl text-ink">Pin your storefront to the top of the directory</h2>
        <p className="text-[#3a3a3a] mt-2 leading-relaxed">
          {isNativeApp() ? "Billed once. " : "$9/month, billed once. "}Your store sorts to the top of <a href="/storefronts" target="_blank" rel="noreferrer" className="underline">/storefronts</a>
          {" "}with a gold <strong>Featured</strong> badge for 30 days. Renews aren&rsquo;t automatic — buy again any time to extend.
        </p>
        <ul className="mt-4 space-y-1.5 text-[13px] text-[#3a3a3a]">
          <li>· Pinned-to-top placement in the directory</li>
          <li>· Gold Featured badge on your card</li>
          <li>· 30-day rotation, no auto-renew</li>
          <li>· Stacks if you buy again before the current window ends</li>
        </ul>
        {isFeatured && featuredUntil ? (
          <div className="mt-5 rounded-lg border border-emerald-300 bg-emerald-50 p-3 text-[13px] text-emerald-900">
            ✓ You&rsquo;re currently featured until <strong>{new Date(featuredUntil).toLocaleDateString()}</strong>. Buying again will extend that date.
          </div>
        ) : null}
        {isIOS() ? (
          <IosPurchaseNotice label="Featured rotation" showPlans={false} className="mt-5" />
        ) : (
        <Button onClick={buy} disabled={busy} className="mt-5 rounded-full bg-gold-gradient text-ink font-semibold h-11 px-6" data-testid="featured-buy">
          {busy ? "Loading…" : isFeatured ? "Extend by 30 days · $29" : "Get featured · $29 for 30 days"}
        </Button>
        )}
      </div>
    </div>
  );
}



// ============================================================ MyStoreCustomOrdersTab
/**
 * Seller-facing dashboard for kennel-storefront custom orders.
 * Mirrors the AdminArmstrong Custom orders tab but scoped to the
 * authenticated seller's store. Buckets:
 *   - Open (requested / quoted / mockup_sent — needs an action from the seller or buyer)
 *   - Paid · ready to ship
 *   - Closed (fulfilled / declined)
 */
function MyStoreCustomOrdersTab({ slug }) {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [quoteTarget, setQuoteTarget] = useState(null);
  const [declineTarget, setDeclineTarget] = useState(null);

  const load = async () => {
    try {
      const { data } = await api.get("/me/store/custom-orders");
      setOrders(data.orders || []);
    } catch {
      toast.error("Failed to load custom orders");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const markFulfilled = async (id) => {
    if (!window.confirm("Mark this custom order as fulfilled (shipped)?")) return;
    try {
      await api.post(`/me/store/custom-orders/${id}/mark-fulfilled`);
      toast.success("Marked fulfilled");
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
  };

  if (loading) return <p className="text-sm text-[#6B6B6B] py-4">Loading…</p>;

  const buckets = {
    open: orders.filter((o) => ["requested", "quoted", "mockup_sent"].includes(o.status)),
    paid: orders.filter((o) => o.status === "paid"),
    closed: orders.filter((o) => ["fulfilled", "declined"].includes(o.status)),
  };

  return (
    <div className="space-y-8 py-4" data-testid="my-store-custom-orders-tab">
      <div className="rounded-2xl border bg-[#FBF7EC]/40 px-5 py-4 flex items-start gap-3 flex-wrap">
        <PaintBrush size={28} weight="duotone" className="text-[#C9A227] flex-shrink-0" />
        <div className="flex-1 min-w-[240px]">
          <p className="text-[10px] font-bold uppercase tracking-[0.22em] text-[#C9A227]">
            How custom orders work
          </p>
          <p className="text-[13px] text-[#3a3a3a] mt-1 leading-relaxed">
            Buyers click your "Personalize" items on the public store and fill out a request form.
            You upload a mockup image somewhere public (Imgur, Drive, your phone&rsquo;s photo upload),
            paste the URL here with a price, and click <strong>Send mockup + price</strong>.
            The buyer gets emailed a link to review and pay. Payment goes straight to your
            Stripe Connect account (Armstrong K9 Registry takes a 5% platform fee, same as
            your regular store sales).
          </p>
        </div>
      </div>

      <CustomOrderSection title="Open · awaiting action" rows={buckets.open} slug={slug}
        onQuote={setQuoteTarget} onDecline={setDeclineTarget} onMarkFulfilled={null}
        testid="my-section-open" emptyCopy="No open requests right now." />
      <CustomOrderSection title="Paid · ready to ship" rows={buckets.paid} slug={slug}
        onQuote={null} onDecline={null} onMarkFulfilled={markFulfilled}
        testid="my-section-paid" emptyCopy="No paid orders waiting on you." />
      <CustomOrderSection title="Closed" rows={buckets.closed} slug={slug}
        onQuote={null} onDecline={null} onMarkFulfilled={null}
        testid="my-section-closed" emptyCopy="No completed custom orders yet." />

      <MyStoreQuoteDialog order={quoteTarget} onClose={() => setQuoteTarget(null)} onSaved={() => { setQuoteTarget(null); load(); }} />
      <MyStoreDeclineDialog order={declineTarget} onClose={() => setDeclineTarget(null)} onSaved={() => { setDeclineTarget(null); load(); }} />
    </div>
  );
}

function CustomOrderSection({ title, rows, slug, onQuote, onDecline, onMarkFulfilled, testid, emptyCopy }) {
  return (
    <section data-testid={testid}>
      <p className="text-[10px] font-bold uppercase tracking-[0.22em] text-[#6B6B6B] mb-3">
        {title} <span className="font-mono ml-1 text-[#9A9A9A]">({rows.length})</span>
      </p>
      {rows.length === 0 ? (
        <p className="text-sm text-[#6B6B6B] italic">{emptyCopy}</p>
      ) : (
        <div className="space-y-3">
          {rows.map((o) => (
            <CustomOrderRow key={o.id} order={o} slug={slug}
              onQuote={onQuote ? () => onQuote(o) : null}
              onDecline={onDecline ? () => onDecline(o) : null}
              onMarkFulfilled={onMarkFulfilled ? () => onMarkFulfilled(o.id) : null} />
          ))}
        </div>
      )}
    </section>
  );
}

function CustomOrderRow({ order, slug, onQuote, onDecline, onMarkFulfilled }) {
  const created = new Date(order.created_at).toLocaleString();
  const buyerUrl = `${webOrigin()}/store/${slug}/custom/${order.id}`;
  const copyLink = async () => {
    if (typeof navigator !== "undefined" && navigator.share) {
      try {
        await navigator.share({ title: "Custom order", url: buyerUrl });
        return;
      } catch (e) {
        if (e?.name === "AbortError") return;
      }
    }
    navigator.clipboard.writeText(buyerUrl);
    toast.success("Link copied");
  };

  return (
    <div className="rounded-xl border bg-white p-4" data-testid={`my-custom-order-${order.id}`}>
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="font-serif text-lg">{order.customer_name}</h3>
            <MyStoreStatusBadge status={order.status} />
          </div>
          <p className="text-[12px] text-[#6B6B6B] mt-0.5">
            <a href={`mailto:${order.customer_email}`} className="underline decoration-[#E5E5E5] hover:text-[#0E0E0E]">{order.customer_email}</a>
            {order.customer_phone ? ` · ${order.customer_phone}` : ""} · {created}
          </p>
        </div>
        <div className="text-right">
          <p className="text-[10px] uppercase tracking-wider text-[#9A9A9A]">Qty</p>
          <p className="font-mono text-[16px]">{order.quantity}</p>
        </div>
      </div>

      <div className="mt-3 grid sm:grid-cols-3 gap-3 text-[12.5px]">
        <div className="sm:col-span-2">
          <p className="text-[10px] uppercase tracking-wider text-[#9A9A9A]">Description</p>
          <p className="mt-0.5 whitespace-pre-wrap leading-relaxed">{order.description}</p>
        </div>
        <div className="space-y-2">
          {order.product_reference && (
            <div>
              <p className="text-[10px] uppercase tracking-wider text-[#9A9A9A]">Base item</p>
              <p className="mt-0.5">{order.product_reference}</p>
            </div>
          )}
          {order.desired_by && (
            <div>
              <p className="text-[10px] uppercase tracking-wider text-[#9A9A9A]">Needed by</p>
              <p className="mt-0.5">{order.desired_by}</p>
            </div>
          )}
          {order.inspiration_url && (
            <div>
              <p className="text-[10px] uppercase tracking-wider text-[#9A9A9A]">Inspiration</p>
              <a href={order.inspiration_url} target="_blank" rel="noreferrer" className="underline break-all text-[#0A6BFF]">{order.inspiration_url}</a>
            </div>
          )}
        </div>
      </div>

      {order.mockup_url && (
        <div className="mt-3">
          <p className="text-[10px] uppercase tracking-wider text-[#9A9A9A] mb-1">Mockup sent</p>
          <img src={order.mockup_url} alt="" className="max-h-32 rounded-md object-contain border bg-[#FAFAFA] p-1" />
        </div>
      )}

      {order.quoted_price_usd && (
        <div className="mt-3 text-[12.5px]">
          <p className="text-[10px] uppercase tracking-wider text-[#9A9A9A]">Quote</p>
          <p className="font-mono mt-0.5">
            ${order.quoted_price_usd.toFixed(2)} × {order.quantity} = ${(order.quoted_price_usd * order.quantity).toFixed(2)}
            {order.status === "paid" && order.amount_cents && (
              <span className="text-emerald-700 ml-2">· Paid (you net ~${((order.amount_cents - (order.application_fee_cents || 0))/100).toFixed(2)})</span>
            )}
          </p>
        </div>
      )}

      {order.decline_reason && order.status === "declined" && (
        <p className="mt-3 text-[12.5px] text-red-700">Decline reason: {order.decline_reason}</p>
      )}

      <div className="mt-4 flex gap-2 flex-wrap">
        <Button size="sm" variant="outline" onClick={copyLink} className="rounded-full h-8 px-3 text-[12px]" data-testid={`my-custom-share-${order.id}`}>
          <ShareNetwork size={12} weight="duotone" className="mr-1" /> Copy buyer link
        </Button>
        {onQuote && !["paid", "fulfilled", "declined"].includes(order.status) && (
          <Button size="sm" onClick={onQuote} className="rounded-full bg-[#0E0E0E] hover:bg-[#1f1f1f] text-white h-8 px-4 text-[12px]" data-testid={`my-custom-quote-${order.id}`}>
            {order.quoted_price_usd ? "Update mockup / price" : "Send mockup + price"}
          </Button>
        )}
        {onMarkFulfilled && order.status === "paid" && (
          <Button size="sm" variant="outline" onClick={onMarkFulfilled} className="rounded-full h-8 px-4 text-[12px]" data-testid={`my-custom-fulfill-${order.id}`}>
            Mark fulfilled
          </Button>
        )}
        {onDecline && ["requested", "quoted", "mockup_sent"].includes(order.status) && (
          <Button size="sm" variant="ghost" onClick={onDecline} className="rounded-full h-8 px-4 text-[12px] text-red-600 hover:text-red-700" data-testid={`my-custom-decline-${order.id}`}>
            Decline
          </Button>
        )}
        <a href={buyerUrl} target="_blank" rel="noreferrer" className="text-[12px] text-[#6B6B6B] hover:text-[#0E0E0E] underline self-center">
          Buyer view ↗
        </a>
      </div>
    </div>
  );
}

function MyStoreStatusBadge({ status }) {
  const map = {
    requested: { cls: "bg-amber-100 text-amber-900", label: "Awaiting mockup", Icon: Hourglass },
    quoted: { cls: "bg-blue-50 text-blue-900", label: "Quote sent", Icon: PaintBrush },
    mockup_sent: { cls: "bg-blue-50 text-blue-900", label: "Quote sent", Icon: PaintBrush },
    paid: { cls: "bg-emerald-100 text-emerald-900", label: "Paid", Icon: CheckCircle },
    fulfilled: { cls: "bg-emerald-100 text-emerald-900", label: "Fulfilled", Icon: CheckCircle },
    declined: { cls: "bg-red-50 text-red-700", label: "Declined", Icon: XCircle },
  };
  const m = map[status] || { cls: "bg-gray-100 text-gray-700", label: status, Icon: PaintBrush };
  const I = m.Icon;
  return (
    <span className={`inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-[0.15em] rounded-full px-2 py-0.5 ${m.cls}`}>
      <I size={10} weight="fill" /> {m.label}
    </span>
  );
}

function MyStoreQuoteDialog({ order, onClose, onSaved }) {
  const [form, setForm] = useState({ quoted_price_usd: "", mockup_url: "", seller_notes: "" });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (order) {
      setForm({
        quoted_price_usd: order.quoted_price_usd || "",
        mockup_url: order.mockup_url || "",
        seller_notes: order.seller_notes || "",
      });
    }
  }, [order]);

  if (!order) return null;

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post(`/me/store/custom-orders/${order.id}/quote`, {
        quoted_price_usd: parseFloat(form.quoted_price_usd),
        mockup_url: form.mockup_url.trim(),
        seller_notes: form.seller_notes.trim() || null,
      });
      toast.success("Quote sent — buyer has been emailed.");
      onSaved();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={!!order} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="my-quote-dialog">
        <DialogHeader>
          <DialogTitle>Send mockup + price to {order.customer_name}</DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <div>
            <Label className="text-xs">Mockup image URL</Label>
            <Input
              required
              value={form.mockup_url}
              onChange={(e) => setForm({ ...form, mockup_url: e.target.value })}
              placeholder="https://imgur.com/..., Drive share link, your photo CDN, etc."
              data-testid="my-quote-mockup"
            />
            <p className="text-[10.5px] text-[#9A9A9A] mt-1">
              Any public image URL works. We&rsquo;ll show it on the buyer&rsquo;s page.
            </p>
          </div>
          <div>
            <Label className="text-xs">Per-unit price (USD)</Label>
            <Input
              required type="number" step="0.01" min="0.01" max="10000"
              value={form.quoted_price_usd}
              onChange={(e) => setForm({ ...form, quoted_price_usd: e.target.value })}
              data-testid="my-quote-price"
            />
            <p className="text-[10.5px] text-[#9A9A9A] mt-1">
              Buyer is charged × {order.quantity}. Armstrong takes a 5% platform fee on the total.
              {form.quoted_price_usd && (
                <span className="block mt-1 text-[#3a3a3a]">
                  Total: ${(parseFloat(form.quoted_price_usd) * order.quantity).toFixed(2)}.
                  You net: ~${(parseFloat(form.quoted_price_usd) * order.quantity * 0.95).toFixed(2)}
                </span>
              )}
            </p>
          </div>
          <div>
            <Label className="text-xs">Notes for the buyer (optional)</Label>
            <Textarea rows={3} value={form.seller_notes} onChange={(e) => setForm({ ...form, seller_notes: e.target.value })} placeholder="Context about the mockup, lead time, materials, etc." data-testid="my-quote-notes" />
          </div>
          <DialogFooter>
            <Button type="submit" disabled={busy} className="rounded-full bg-[#0E0E0E] hover:bg-[#1f1f1f] text-white w-full" data-testid="my-quote-submit">
              {busy ? "Sending…" : "Send to buyer →"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function MyStoreDeclineDialog({ order, onClose, onSaved }) {
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  if (!order) return null;
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post(`/me/store/custom-orders/${order.id}/decline`, { reason });
      toast.success("Buyer has been notified");
      onSaved();
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open={!!order} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="my-decline-dialog">
        <DialogHeader><DialogTitle>Decline request from {order.customer_name}</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <div>
            <Label className="text-xs">Reason (shown to buyer)</Label>
            <Textarea rows={4} value={reason} onChange={(e) => setReason(e.target.value)} placeholder="We can't take this on right now because…" data-testid="my-decline-reason" />
          </div>
          <DialogFooter>
            <Button type="submit" disabled={busy} variant="destructive" className="rounded-full w-full" data-testid="my-decline-submit">
              {busy ? "Sending…" : "Decline and notify buyer"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
